import { Col } from 'antd';
import classNames from 'classnames';
import { get, includes, isEmpty } from 'lodash-es';
import { Fragment, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  getShipmentDetailSummaryView,
  setShipmentDetailSummaryView,
} from '../../app/redux/shipmentDetail';
import { useReduxDialogControls } from '../../common/utils/dialogUtils';
import { useCachedProp, useMountEffect } from '../../common/utils/hookUtils';
import {
  Scrollbars,
  VerticalTrackTypes,
} from '../../components/layout/Scrollbars';
import {
  Flex1,
  FlexCol,
  TwoLines,
  TwoLinesSeparator,
} from '../../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../../components/layout/layoutElements.styles';
import SegmentedControl from '../../components/nav/SegmentedControl';
import { SlidingContent } from '../../components/nav/SlidingContent';
import { useResponsiveQueries } from '../../components/responsive/responsiveQueries';
import StickyElement from '../../components/scroll/StickyElement';
import { LabelRow } from '../../components/typography';
import { cssVariables } from '../../styles/cssVariables';
import { ShipmentAssetsInventoryDrawer } from './ShipmentAssetsInventory';
import {
  ServiceAdvisoryCard,
  ServiceUpdateCard,
} from './shipmentSummaries/ServiceUpdateCard';
import { ShipmentHistoryAndDocs } from './shipmentSummaries/ShipmentHistoryAndDocs';
import { ShipmentOrderSummary } from './shipmentSummaries/ShipmentOrderSummary';
import { ShipmentPackageList } from './shipmentSummaries/ShipmentPackageList';
import {
  ShipmentSummaryDateTimeFormatFlight,
  ShipmentSummaryLabelWithValue,
  TwoLineLabelWithValue,
} from './shipmentSummaries/shipmentSummariesCommon';
import { CopyToClipboardButton } from '../../components/widgets/CopyToClipboard';
import { useCardElementsStyles } from '../../components/layout/cardElements.styles';
import { useShipmentDetailSharedStyles } from './shipmentDetailShared.styles';
import { useShipmentSummariesStyles } from './shipmentSummaries.styles';

export const SHIPMENT_SUMMARY_VIEWS = [
  {
    name: 'order-summary',
    labelId: 'shipmentDetail.orderSummary',
    component: ShipmentOrderSummary,
    props: {
      'data-subject': 'shipment-detail',
      'data-role': 'tab',
      'data-id': 'order-summary',
    },
  },
  {
    name: 'package-list',
    labelId: 'shipmentDetail.packageList',
    component: ShipmentPackageList,
    props: {
      'data-subject': 'shipment-detail',
      'data-role': 'tab',
      'data-id': 'package-list',
    },
  },
  {
    name: 'history-docs',
    labelId: 'shipmentDetail.historyDocs',
    component: ShipmentHistoryAndDocs,
    props: {
      'data-subject': 'shipment-detail',
      'data-role': 'tab',
      'data-id': 'history-docs',
    },
  },
].map(v => ({ ...v, value: v.name }));

const InnerScrollbars = props => (
  <Scrollbars {...props} vertical={VerticalTrackTypes.OUTSIDE} />
);

export function ShipmentSummaries({ className, shipment, views, sharedProps }) {
  const { styles } = useCardElementsStyles();
  const { styles: shipmentSummariesStyles } = useShipmentSummariesStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const media = useResponsiveQueries();
  const isSm = media.xs || media.sm;
  const cachedViews = useCachedProp(views);
  const viewsInfo = useMemo(
    () =>
      SHIPMENT_SUMMARY_VIEWS.filter(
        ({ name }) => !cachedViews || includes(cachedViews, name)
      ),
    [cachedViews]
  );

  const summaryView = useSelector(getShipmentDetailSummaryView);
  const dispatch = useDispatch();
  const setSummaryView = useCallback(
    value => dispatch(setShipmentDetailSummaryView(value)),
    [dispatch]
  );

  const Wrapper = isSm ? Fragment : InnerScrollbars;

  const isDelivered = shipment?.deliveryDateTime?.actual;
  const hasServiceUpdate = shipment?.serviceUpdate;
  const hasServiceAdvisory = !isEmpty(shipment?.serviceAdvisory);
  // const hasAssets = shipment?.assets?.length > 0;
  // const hasAssets = false; // QOL2-1969: Hide Inventory Button

  const assetsDrawer = useReduxDialogControls({
    id: 'shipmentDetail.assetsDrawer',
  });
  // Close drawer if detail is left
  useMountEffect(() => () => assetsDrawer.close());

  return (
    <FlexCol
      className={classNames(
        shipmentSummariesStyles.shipmentSummaries,
        layoutStyles.flex1,
        className
      )}
    >
      <StickyElement
        innerClassName={shipmentSummariesStyles.stickyElementInner}
      >
        <div
          className={classNames(
            styles.simpleCard,
            'SimpleCard',
            'SimpleCardTheme-ROUND_BORDERED'
          )}
        >
          <div className="SimpleCard-Header">
            <SegmentedControl
              options={viewsInfo.map(v => ({
                ...v,
                className: shipmentSummariesStyles.segmentedControlSegment,
              }))}
              value={summaryView}
              onChange={setSummaryView}
              style={{
                backgroundColor: 'transparent',
                padding: `${cssVariables.spaceXs} ${cssVariables.spaceNorm}`,
              }}
            />
          </div>
          <TwoLines className="size-10" />
        </div>
        <LabelRow noBorder>
          <Col>
            <div className={shipmentSummariesStyles.jobNumberRow}>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetails.jobNumber"
                text={get(shipment, 'jobNumber')}
                valueProps={{ 'data-info-id': 'jobNumber' }}
              />
              {shipment?.jobNumber && (
                <CopyToClipboardButton
                  tooltipId="job.jobNumber.copy"
                  className="icon-11"
                  iconWrapperClassName={
                    shipmentSummariesStyles.copyToClipboardIcon
                  }
                  getContent={() => shipment.jobNumber}
                />
              )}
            </div>
          </Col>
          <Col>
            {get(shipment, 'quoteNumber') && (
              <div className={shipmentSummariesStyles.jobNumberRow}>
                <ShipmentSummaryLabelWithValue
                  labelId="shipmentDetails.quoteNumber"
                  text={get(shipment, 'quoteNumber')}
                />
                {shipment?.quoteNumber && (
                  <CopyToClipboardButton
                    tooltipId="job.jobNumber.copy"
                    className="icon-11"
                    iconWrapperClassName={
                      shipmentSummariesStyles.copyToClipboardIcon
                    }
                    getContent={() => shipment.quoteNumber}
                  />
                )}
              </div>
            )}
          </Col>
        </LabelRow>
        {isDelivered && (
          <>
            <TwoLinesSeparator />
            <div className="padding-hor-norm">
              <div className={shipmentSummariesStyles.deliveryProof}>
                <FormattedMessage id="shipmentDetail.deliveryProof" />
              </div>
              <LabelRow>
                <Col>
                  <TwoLineLabelWithValue
                    size="md"
                    labelId="shipmentDetail.deliveryTime"
                    text={
                      <ShipmentSummaryDateTimeFormatFlight
                        value={shipment.deliveryDateTime.actual}
                      />
                    }
                  />
                </Col>
                <Col>
                  <TwoLineLabelWithValue
                    labelId="shipmentDetail.signedBy"
                    text={get(shipment, 'signedBy')}
                  />
                </Col>
              </LabelRow>
            </div>
          </>
        )}
        {hasServiceUpdate && (
          <>
            <TwoLinesSeparator />
            <ServiceUpdateCard shipment={shipment} />
          </>
        )}
        <TwoLines className="size-10 margin-bottom-norm" />
      </StickyElement>
      <Flex1>
        <Wrapper>
          {hasServiceAdvisory && <ServiceAdvisoryCard shipment={shipment} />}
          <SlidingContent
            className={shipmentSummariesStyles.slidingContentOverride}
            autoHeight
            listenToSelected
            selected={summaryView}
            onSelect={setSummaryView}
            data={viewsInfo}
            sharedProps={{ ...sharedProps, shipment }}
          />
        </Wrapper>
      </Flex1>
      <ShipmentAssetsInventoryDrawer
        isOpen={assetsDrawer.isOpen}
        close={assetsDrawer.close}
        data={shipment?.assets}
      />
    </FlexCol>
  );
}

export function ShipmentSummariesCol({ colClassName, ...rest }) {
  const { styles } = useShipmentDetailSharedStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  return (
    <Col
      className={classNames(
        styles.leftColumn,
        layoutStyles.flexCol,
        colClassName
      )}
    >
      <ShipmentSummaries {...rest} />
    </Col>
  );
}
